import {
  BAR_FADE_SHAPE_LABELS,
  BAR_FADE_SHAPES,
  BAR_MEDIA_ACCEPT,
  BAR_MEDIA_MAX_MB,
  DEFAULT_BAR_FADE,
  NEWS_PLACEMENT_LABELS,
  NEWS_PLACEMENTS,
  type BarFade,
  type BarSettings,
  type NewsBarBusyDto,
  type NewsDto,
  type NewsInput,
} from '@edms/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useLayoutEffect, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { RichInline, RichText, RichTextEditor } from '../components/rich-text';
import { BarBanner } from '../components/top-bar';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Icon } from '../lib/icons';
import { fromLocalInput, toLocalInput } from '../lib/tasks-ui';
import { Empty, useToast } from '../lib/ui';

const dayFmt = new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' });

/** Дата новости для поля ввода: ГГГГ-ММ-ДД в местном времени. */
function localDate(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function NewsItem({ n, clamped, onEdit }: { n: NewsDto; clamped?: boolean; onEdit?: () => void }) {
  const body = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState(false);
  // Кнопка нужна, только если текст действительно не влез в три строки.
  const [overflows, setOverflows] = useState(false);
  useLayoutEffect(() => {
    const el = body.current;
    if (!clamped || !el) return;
    const check = () => setOverflows(el.scrollHeight > el.clientHeight + 1);
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, [clamped, n.body]);
  return (
    <article className={`card card-pad news-item${clamped && !expanded ? ' clamped' : ''}${overflows ? ' overflows' : ''}`}>
      <h3>
        {n.pinned && <Icon name="pin" size={16} color="var(--brand)" aria-label="закреплено" />}
        <RichInline text={n.title} />
      </h3>
      <span className="faint">
        {dayFmt.format(new Date(n.publishedAt))}
        {n.author ? ` · ${n.author.fullName}` : ''}
      </span>
      <div className="news-body" ref={body} id={`news-${n.id}`}>
        <RichText text={n.body} />
      </div>
      {clamped && (overflows || expanded) && (
        <button type="button" className="link-btn news-toggle" aria-expanded={expanded} aria-controls={`news-${n.id}`} onClick={() => setExpanded((v) => !v)}>
          {expanded ? 'Свернуть' : 'Развернуть'}
        </button>
      )}
      {onEdit && (
        <div>
          <button className="btn btn-ghost" onClick={onEdit}>
            Изменить
          </button>
        </div>
      )}
    </article>
  );
}

/** Последние новости на главной. */
export function NewsWidget() {
  const news = useQuery({ queryKey: ['news', 'latest'], queryFn: () => api.get<NewsDto[]>('/news', { limit: 3 }) });
  if (!news.data || news.data.length === 0) return null;
  return (
    <section style={{ marginTop: 20 }}>
      <div className="section-head">
        <h2>Новости</h2>
        <Link to="/news">все →</Link>
      </div>
      <div className="news-list">
        {news.data.map((n) => (
          <NewsItem key={n.id} n={n} clamped />
        ))}
      </div>
    </section>
  );
}

type FormState = Required<Omit<NewsInput, 'bar' | 'barFrom' | 'barUntil' | 'barReplace'>> & {
  bar: BarSettings;
  /** Поля «с» и «до» в местном времени (ГГГГ-ММ-ДДTчч:мм); пусто — сразу / бессрочно. */
  barFrom: string;
  barUntil: string;
};

const DEFAULT_BAR: BarSettings = { text: '', caption: '', eventDate: null, countdown: true, dismissible: true, pos: 50, dim: 55, fade: DEFAULT_BAR_FADE };

const EMPTY = (): FormState => ({
  title: '',
  body: '',
  pinned: false,
  archived: false,
  publishedAt: localDate(new Date().toISOString()),
  placement: 'news',
  bar: DEFAULT_BAR,
  barFrom: '',
  barUntil: '',
});

function fromDto(n: NewsDto): FormState {
  const { from, until, media: _media, ...bar } = n.bar ?? { ...DEFAULT_BAR, from: null, until: null, media: null };
  return {
    title: n.title,
    body: n.body,
    pinned: n.pinned,
    archived: n.archived,
    publishedAt: localDate(n.publishedAt),
    placement: n.placement,
    bar,
    barFrom: toLocalInput(from),
    barUntil: toLocalInput(until),
  };
}

/** Фон из формы: новый файл (ещё не загружен) или сохранённый. */
interface MediaState {
  url: string;
  mime: string;
  name: string;
  size: number;
  file: File | null;
}

const KB = 1024;
const sizeLabel = (n: number) => (n >= KB * KB ? `${(n / KB / KB).toFixed(1)} МБ` : `${Math.max(1, Math.round(n / KB))} КБ`);
const MEDIA_KIND = (mime: string) => (mime.startsWith('video/') ? 'Видео' : mime === 'image/gif' ? 'GIF' : 'Картинка');
const dateTimeFmt = new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' });

/** Фон панели: загрузка, сведения о кадре и миниатюра. */
function MediaPicker({ media, onChange }: { media: MediaState | null; onChange: (m: MediaState | null) => void }) {
  const input = useRef<HTMLInputElement>(null);
  const toast = useToast();
  const [info, setInfo] = useState<{ w: number; h: number; duration: number | null } | null>(null);
  useEffect(() => setInfo(null), [media?.url]);
  const pick = (f: File | undefined) => {
    if (!f) return;
    if (!BAR_MEDIA_ACCEPT.split(',').includes(f.type)) return toast('Подойдёт картинка (JPG, PNG, WebP), GIF или видео (MP4, WebM)', { error: true });
    if (f.size > BAR_MEDIA_MAX_MB * KB * KB) return toast(`Файл больше ${BAR_MEDIA_MAX_MB} МБ`, { error: true });
    onChange({ url: URL.createObjectURL(f), mime: f.type, name: f.name, size: f.size, file: f });
  };
  const video = media?.mime.startsWith('video/');
  return (
    <div className="field">
      <span>Фон</span>
      {media ? (
        <div className="media-chip">
          {video ? (
            <video src={media.url} muted loop autoPlay playsInline onLoadedMetadata={(e) => setInfo({ w: e.currentTarget.videoWidth, h: e.currentTarget.videoHeight, duration: e.currentTarget.duration })} />
          ) : (
            <img src={media.url} alt="" onLoad={(e) => setInfo({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight, duration: null })} />
          )}
          <span className="media-chip-info">
            <b>{media.name || 'Фон'}</b>
            <span className="faint">
              {MEDIA_KIND(media.mime)}
              {info?.duration ? ` · ${Math.round(info.duration)} с` : ''}
              {info ? ` · ${info.w}×${info.h}` : ''} · {sizeLabel(media.size)}
              {video || media.mime === 'image/gif' ? ' · без звука, по кругу' : ''}
            </span>
            {info && info.w < 1200 && <span className="field-error">Узкий кадр — на широком экране может быть нерезким. Лучше от 1600 px в ширину.</span>}
          </span>
          <button type="button" className="btn btn-sm" onClick={() => input.current?.click()}>
            Заменить
          </button>
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => onChange(null)}>
            Убрать
          </button>
        </div>
      ) : (
        <div>
          <button type="button" className="btn" onClick={() => input.current?.click()}>
            <Icon name="plus" size={16} /> Загрузить картинку, GIF или видео
          </button>
        </div>
      )}
      <span className="faint">
        JPG, PNG, WebP, GIF, MP4 или WebM до {BAR_MEDIA_MAX_MB} МБ. Панель — узкая полоса, из кадра видна полоса по ширине; какую часть показать — ниже. Без фона — фирменный
        цвет EDMS.
      </span>
      <input
        ref={input}
        type="file"
        accept={BAR_MEDIA_ACCEPT}
        hidden
        onChange={(e) => {
          pick(e.target.files?.[0]);
          e.target.value = '';
        }}
      />
    </div>
  );
}

/** Настройки и предпросмотр публикации в верхней панели. */
function BarFields({ v, setV, media, setMedia, errors }: { v: FormState; setV: (f: (x: FormState) => FormState) => void; media: MediaState | null; setMedia: (m: MediaState | null) => void; errors: Record<string, string> }) {
  const bar = v.bar;
  const set = (patch: Partial<BarSettings>) => setV((x) => ({ ...x, bar: { ...x.bar, ...patch } }));
  const fade = (patch: Partial<BarFade>) => set({ fade: { ...bar.fade, ...patch } });
  const preview = media ? { url: media.url, mime: media.mime } : null;
  return (
    <fieldset className="bar-fields">
      <legend>Верхняя панель</legend>
      <div className="two-cols">
        <label className="field">
          <span>Текст в панели</span>
          <input className="input" value={bar.text} maxLength={60} onChange={(e) => set({ text: e.target.value })} placeholder="С Днём народного единства!" aria-invalid={Boolean(errors['bar.text'])} />
          {errors['bar.text'] ? <span className="field-error">{errors['bar.text']}</span> : <span className="faint">{bar.text.length} из 60 · на телефоне видна только эта строка, целиком — около 20 знаков</span>}
        </label>
        <label className="field">
          <span>Подпись (необязательно)</span>
          <input className="input" value={bar.caption} maxLength={80} onChange={(e) => set({ caption: e.target.value })} placeholder="4 ноября — выходной день" />
          <span className="faint">Только на компьютере</span>
        </label>
      </div>
      <div className="two-cols">
        <label className="field">
          <span>Дата события (необязательно)</span>
          <input
            className="input"
            type="date"
            value={bar.eventDate ?? ''}
            onChange={(e) => {
              const eventDate = e.target.value || null;
              // Праздник — по умолчанию показывать до конца его дня.
              setV((x) => ({ ...x, bar: { ...x.bar, eventDate }, barUntil: x.barUntil || (eventDate ? `${eventDate}T23:59` : '') }));
            }}
          />
          <label className="check">
            <input type="checkbox" checked={bar.countdown} disabled={!bar.eventDate} onChange={(e) => set({ countdown: e.target.checked })} />
            Пометка «через 2 дня / завтра / сегодня»
          </label>
        </label>
        <div className="field">
          <span>Скрытие</span>
          <label className="check">
            <input type="checkbox" checked={bar.dismissible} onChange={(e) => set({ dismissible: e.target.checked })} />
            Сотрудник может скрыть у себя (малозаметный крестик)
          </label>
          <span className="faint">Скрытая публикация больше не показывается этому сотруднику; следующие — показываются.</span>
        </div>
      </div>
      <MediaPicker media={media} onChange={setMedia} />
      <div className="field">
        <span>Подгонка</span>
        <label className="range-row">
          <span>Часть кадра</span>
          <input type="range" min={0} max={100} value={bar.pos} onChange={(e) => set({ pos: Number(e.target.value) })} disabled={!media} />
          <span className="faint">{bar.pos < 34 ? 'верх' : bar.pos > 66 ? 'низ' : 'середина'}</span>
        </label>
        <label className="range-row">
          <span>Затемнение под текстом</span>
          <input type="range" min={0} max={90} value={bar.dim} onChange={(e) => set({ dim: Number(e.target.value) })} />
          <span className="faint">{bar.dim}%</span>
        </label>
      </div>
      <div className="field">
        <span>Растворение краёв</span>
        <div className="fade-row">
          {(['left', 'right', 'bottom'] as const).map((side) => (
            <label key={side} className="check">
              <input type="checkbox" checked={bar.fade[side]} onChange={(e) => fade({ [side]: e.target.checked })} />
              {{ left: 'Слева', right: 'Справа', bottom: 'Снизу' }[side]}
            </label>
          ))}
        </div>
        <div className="seg" role="radiogroup" aria-label="Форма края">
          {BAR_FADE_SHAPES.map((shape) => (
            <button key={shape} type="button" role="radio" aria-checked={bar.fade.shape === shape} className={bar.fade.shape === shape ? 'on' : undefined} onClick={() => fade({ shape })}>
              {BAR_FADE_SHAPE_LABELS[shape]}
            </button>
          ))}
        </div>
        <label className="range-row">
          <span>Глубина</span>
          <input type="range" min={8} max={240} value={bar.fade.depth} onChange={(e) => fade({ depth: Number(e.target.value) })} />
          <span className="faint">{bar.fade.depth} px</span>
        </label>
      </div>
      <div className="field">
        <span>Предпросмотр — компьютер</span>
        <div className="bar-preview">
          <BarBanner bar={bar.text ? bar : { ...bar, text: 'Текст в панели' }} media={preview} onDismiss={bar.dismissible ? () => undefined : undefined} />
          <span className="bar-preview-icons" aria-hidden>
            <Icon name="bell" size={18} />
            <Icon name="moon" size={18} />
          </span>
        </div>
      </div>
      <div className="field">
        <span>Телефон</span>
        <div className="bar-preview phone">
          <BarBanner compact bar={bar.text ? bar : { ...bar, text: 'Текст в панели' }} media={preview} onDismiss={bar.dismissible ? () => undefined : undefined} />
          <span className="bar-preview-icons" aria-hidden>
            <Icon name="bell" size={18} />
            <Icon name="moon" size={18} />
            <Icon name="logout" size={18} />
          </span>
        </div>
      </div>
      <div className="two-cols">
        <label className="field">
          <span>Показывать с</span>
          <input id="bar-from" className="input" type="datetime-local" value={v.barFrom} onChange={(e) => setV((x) => ({ ...x, barFrom: e.target.value }))} />
          <span className="faint">Пусто — сразу после публикации</span>
        </label>
        <label className="field">
          <span>Показывать до</span>
          <input className="input" type="datetime-local" value={v.barUntil} onChange={(e) => setV((x) => ({ ...x, barUntil: e.target.value }))} aria-invalid={Boolean(errors.barUntil)} />
          {errors.barUntil ? <span className="field-error">{errors.barUntil}</span> : <span className="faint">После этого панель очистится сама (или покажет следующую). Пусто — пока не заменят.</span>}
        </label>
      </div>
    </fieldset>
  );
}

/** Панель в это время занята: заменить, показать после или выбрать другое время. */
function BusyDialog({ busy, onReplace, onAfter, onPick }: { busy: NewsBarBusyDto[]; onReplace: () => void; onAfter: (() => void) | null; onPick: () => void }) {
  const first = busy[0]!;
  return (
    <>
      <div className="drawer-backdrop" onClick={onPick} />
      <div className="dialog" role="dialog" aria-modal="true" aria-labelledby="busy-title">
        <h2 id="busy-title">В верхней панели уже есть публикация</h2>
        <p className="muted">
          «{first.text}» — {first.until ? `показывается до ${dateTimeFmt.format(new Date(first.until))}` : 'без срока окончания'}
          {busy.length > 1 ? ` и ещё ${busy.length - 1} запланировано в это время` : ''}.
        </p>
        <div className="dialog-actions">
          <button className="btn btn-primary" onClick={onReplace}>
            Заменить сейчас
          </button>
          {onAfter && (
            <button className="btn" onClick={onAfter}>
              Показать после неё — с {dateTimeFmt.format(new Date(busy.reduce((m, b) => (b.until! > m ? b.until! : m), busy[0]!.until!)))}
            </button>
          )}
          <button className="btn btn-ghost" onClick={onPick}>
            Выбрать другое время
          </button>
        </div>
        <p className="faint">Заменённая публикация не удаляется: её можно снова показать на вкладке «Верхняя панель».</p>
      </div>
    </>
  );
}

function NewsForm({ initial, onDone }: { initial: NewsDto | null; onDone: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [v, setV] = useState<FormState>(() => (initial ? fromDto(initial) : EMPTY()));
  const saved = initial?.bar?.media ?? null;
  const [media, setMediaState] = useState<MediaState | null>(saved ? { ...saved, file: null } : null);
  const [busy, setBusy] = useState<NewsBarBusyDto[] | null>(null);
  // Ссылки на выбранные, но не загруженные файлы освобождаем.
  const setMedia = (m: MediaState | null) =>
    setMediaState((old) => {
      if (old?.file) URL.revokeObjectURL(old.url);
      return m;
    });
  const latest = useRef(media);
  latest.current = media;
  useEffect(
    () => () => {
      if (latest.current?.file) URL.revokeObjectURL(latest.current.url);
    },
    [],
  );
  const onBar = v.placement !== 'news';

  const payload = (state: FormState, replace: boolean): NewsInput => ({
    title: state.title,
    body: state.body,
    pinned: state.placement === 'bar' ? false : state.pinned,
    archived: state.archived,
    publishedAt: state.publishedAt,
    placement: state.placement,
    ...(state.placement === 'news'
      ? {}
      : { bar: state.bar, barFrom: fromLocalInput(state.barFrom), barUntil: fromLocalInput(state.barUntil), barReplace: replace }),
  });
  const save = useMutation({
    mutationFn: async ({ state, replace }: { state: FormState; replace: boolean }) => {
      const body = payload(state, replace);
      const row = initial ? await api.patch<NewsDto>(`/news/${initial.id}`, body) : await api.post<NewsDto>('/news', body);
      if (state.placement !== 'news') {
        if (media?.file) {
          const form = new FormData();
          form.set('file', media.file);
          await api.upload(`/news/${row.id}/bar-media`, form);
        } else if (!media && saved) {
          await api.delete(`/news/${row.id}/bar-media`);
        }
      }
      return row;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['news'] });
      toast(initial ? 'Публикация сохранена' : 'Опубликовано');
      onDone();
    },
  });
  const remove = useMutation({
    mutationFn: () => api.delete(`/news/${initial!.id}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['news'] });
      toast('Публикация удалена');
      onDone();
    },
  });
  const err = save.error instanceof ApiError ? save.error : null;
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (onBar && !v.archived) {
      // Сначала спросить, если панель в это время занята.
      const taken = await api.get<NewsBarBusyDto[]>('/news/bar/busy', {
        from: fromLocalInput(v.barFrom) ?? new Date().toISOString(),
        until: fromLocalInput(v.barUntil),
        except: initial?.id,
      });
      if (taken.length > 0) return setBusy(taken);
    }
    save.mutate({ state: v, replace: false });
  };
  const after = busy?.every((b) => b.until) ? busy.reduce((m, b) => (b.until! > m ? b.until! : m), busy[0]!.until!) : null;

  return (
    <form className="card card-pad" style={{ display: 'grid', gap: 14 }} onSubmit={(e) => void submit(e)}>
      <h2>{initial ? 'Изменить публикацию' : 'Новая публикация'}</h2>
      {err && !Object.keys(err.fields).length && <div className="alert">{err.message}</div>}
      <div className="field">
        <span>Где опубликовать</span>
        <div className="seg" role="radiogroup" aria-label="Где опубликовать">
          {NEWS_PLACEMENTS.map((p) => (
            <button key={p} type="button" role="radio" aria-checked={v.placement === p} className={v.placement === p ? 'on' : undefined} onClick={() => setV((x) => ({ ...x, placement: p }))}>
              {NEWS_PLACEMENT_LABELS[p]}
            </button>
          ))}
        </div>
      </div>
      <div className="field">
        <span>Заголовок</span>
        <RichTextEditor inline label="Заголовок" value={v.title} onChange={(title) => setV((x) => ({ ...x, title }))} minHeight={0} placeholder="О чём публикация" />
        {err?.fields.title && <span className="field-error">{err.fields.title}</span>}
      </div>
      <div className="field">
        <span>{v.placement === 'bar' ? 'Полный текст (необязательно) — откроется по нажатию на панель' : 'Текст'}</span>
        <RichTextEditor label="Текст" value={v.body} onChange={(body) => setV((x) => ({ ...x, body }))} minHeight={v.placement === 'bar' ? 90 : 160} placeholder="Можно выделить жирным, добавить цитату, список или ссылку" />
        {err?.fields.body && <span className="field-error">{err.fields.body}</span>}
      </div>
      {v.placement !== 'bar' && (
        <>
          <label className="field" style={{ maxWidth: 220 }}>
            <span>Дата новости</span>
            <input className="input" type="date" required value={v.publishedAt} onChange={(e) => setV({ ...v, publishedAt: e.target.value })} aria-invalid={Boolean(err?.fields.publishedAt)} />
            {err?.fields.publishedAt && <span className="field-error">{err.fields.publishedAt}</span>}
          </label>
          <label className="check">
            <input type="checkbox" checked={v.pinned} onChange={(e) => setV({ ...v, pinned: e.target.checked })} />
            Закрепить сверху
          </label>
        </>
      )}
      {onBar && <BarFields v={v} setV={setV} media={media} setMedia={setMedia} errors={err?.fields ?? {}} />}
      {initial && (
        <label className="check">
          <input type="checkbox" checked={v.archived} onChange={(e) => setV({ ...v, archived: e.target.checked })} />
          В архив — не показывать ни в ленте, ни в панели
        </label>
      )}
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button className="btn btn-primary" disabled={save.isPending || !v.title.trim() || (v.placement !== 'bar' && !v.body.trim()) || (onBar && !v.bar.text.trim())}>
          {save.isPending ? 'Сохраняем…' : initial ? 'Сохранить' : 'Опубликовать'}
        </button>
        <button type="button" className="btn btn-ghost" onClick={onDone}>
          Отмена
        </button>
        {initial && (
          <button
            type="button"
            className="btn btn-ghost"
            style={{ marginLeft: 'auto', color: 'var(--danger)' }}
            disabled={remove.isPending}
            onClick={() => confirm('Удалить публикацию безвозвратно?') && remove.mutate()}
          >
            Удалить
          </button>
        )}
      </div>
      {busy && (
        <BusyDialog
          busy={busy}
          onReplace={() => {
            setBusy(null);
            save.mutate({ state: v, replace: true });
          }}
          onAfter={
            after
              ? () => {
                  const next = { ...v, barFrom: toLocalInput(after) };
                  setBusy(null);
                  setV(next);
                  save.mutate({ state: next, replace: false });
                }
              : null
          }
          onPick={() => {
            setBusy(null);
            document.getElementById('bar-from')?.focus();
          }}
        />
      )}
    </form>
  );
}

/** Публикация верхней панели в списке: как выглядит и когда показывается. */
function BarItem({ n, onEdit }: { n: NewsDto; onEdit?: () => void }) {
  const bar = n.bar!;
  const now = Date.now();
  const from = bar.from ? Date.parse(bar.from) : null;
  const until = bar.until ? Date.parse(bar.until) : null;
  const status =
    from !== null && until !== null && until <= from
      ? { label: 'Отменена заменой', cls: '' }
      : until !== null && until <= now
        ? { label: `Завершена ${dateTimeFmt.format(until)}`, cls: '' }
        : from !== null && from > now
          ? { label: `Запланирована с ${dateTimeFmt.format(from)}${until ? ` до ${dateTimeFmt.format(until)}` : ''}`, cls: 'badge-info' }
          : { label: `Показывается сейчас${until ? ` · до ${dateTimeFmt.format(until)}` : ''}`, cls: 'badge-brand' };
  return (
    <article className="card card-pad news-item">
      <div className="bar-preview">
        <BarBanner bar={bar} />
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <span className={`badge ${status.cls}`}>{status.label}</span>
        <span className="faint">
          <RichInline text={n.title} />
          {n.placement === 'both' ? ' · и в ленте новостей' : ''}
        </span>
        {onEdit && (
          <button className="btn btn-ghost btn-sm" style={{ marginLeft: 'auto' }} onClick={onEdit}>
            Изменить
          </button>
        )}
      </div>
    </article>
  );
}

export function NewsPage() {
  const { can } = useAuth();
  const manage = can('news.manage');
  const [tab, setTab] = useState<'feed' | 'bar' | 'archive'>('feed');
  const [editing, setEditing] = useState<NewsDto | 'new' | null>(null);
  const news = useQuery({
    queryKey: ['news', 'list', tab],
    queryFn: () => api.get<NewsDto[]>('/news', { archive: tab === 'archive', bar: tab === 'bar', limit: 200 }),
  });
  type Tab = 'feed' | 'bar' | 'archive';
  const tabs: Array<{ key: Tab; label: string }> = [
    { key: 'feed', label: 'Лента' },
    ...(manage ? [{ key: 'bar' as const, label: 'Верхняя панель' }] : []),
    { key: 'archive', label: 'Архив' },
  ];

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Новости</h1>
          <p className="muted">Объявления для всех сотрудников — в ленте и в верхней панели</p>
        </div>
        {manage && editing === null && (
          <button className="btn btn-primary" onClick={() => setEditing('new')}>
            <Icon name="plus" size={18} /> Написать
          </button>
        )}
      </div>
      <div className="tabs" role="tablist">
        {tabs.map((t) => (
          <button key={t.key} role="tab" aria-selected={tab === t.key} className={`tab${tab === t.key ? ' active' : ''}`} onClick={() => setTab(t.key)}>
            {t.label}
          </button>
        ))}
      </div>
      <div className="news-list" style={{ maxWidth: 980 }}>
        {editing !== null && <NewsForm key={editing === 'new' ? 'new' : editing.id} initial={editing === 'new' ? null : editing} onDone={() => setEditing(null)} />}
        {news.data?.length === 0 && (
          <Empty emoji={tab === 'bar' ? '🎏' : '📰'} title={tab === 'archive' ? 'Архив пуст' : tab === 'bar' ? 'В верхней панели ничего не публиковали' : 'Новостей пока нет'} />
        )}
        {news.data?.map((n) =>
          tab === 'bar' && n.bar ? (
            <BarItem key={n.id} n={n} onEdit={manage ? () => setEditing(n) : undefined} />
          ) : (
            <NewsItem key={n.id} n={n} onEdit={manage ? () => setEditing(n) : undefined} />
          ),
        )}
      </div>
    </>
  );
}
